import React from 'react'
import { Button, Card, Form, Modal, Popconfirm, Select, Space, Table, Tag as AntTag, Typography, message } from 'antd'
import { listShops } from '../../api/shops'
import { listShopTags } from '../../api/shopTags'
import { listShopTagMappings, createShopTagMapping, deleteShopTagMapping } from '../../api/shopTagsMap'

export default function ShopTagMapsAdmin() {
  const [loading, setLoading] = React.useState(false)
  const [shops, setShops] = React.useState([])
  const [categories, setCategories] = React.useState([])
  const [mappings, setMappings] = React.useState([])

  const [selectedShopId, setSelectedShopId] = React.useState(null)

  const [createOpen, setCreateOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [createForm] = Form.useForm()

  const fetchAll = React.useCallback(async (shopId) => {
    setLoading(true)
    try {
      const [shopsList, catList, maps] = await Promise.all([
        listShops(),
        listShopTags(),
        listShopTagMappings(shopId ? { shopId } : {}),
      ])
      setShops(shopsList || [])
      setCategories(catList || [])
      setMappings(maps || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchAll(selectedShopId) }, [fetchAll, selectedShopId])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^[\s"']+|[\s"']+$/g, '')
  }

  function currentShop() {
    return shops.find(s => s.id === selectedShopId) || null
  }

  function categoriesForSelectedShop() {
    if (!selectedShopId) return []
    // BE returns either [{shop, category}] or flat [{id, tagAr, ...}] when filtered by shopId
    if (Array.isArray(mappings) && mappings.length && mappings[0]?.category) {
      return mappings.filter(m => m.shop?.id === selectedShopId).map(m => m.category)
    }
    if (Array.isArray(mappings) && mappings.length && mappings[0]?.id && mappings[0]?.tagAr !== undefined) {
      return mappings
    }
    const s = currentShop()
    return s?.mainCategory ? [s.mainCategory] : []
  }

  function availableCategories() {
    const assignedIds = new Set(categoriesForSelectedShop().map(c => c.id))
    return categories.filter(c => !assignedIds.has(c.id))
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const payload = { shopId: selectedShopId || values.shopId, categoryId: values.categoryId }
      if (!payload.shopId) throw new Error('يرجى اختيار متجر')
      await createShopTagMapping(payload)
      message.success('تمت إضافة التصنيف إلى المتجر')
      setCreateOpen(false)
      createForm.resetFields()
      await fetchAll(selectedShopId)
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  async function onRemove(categoryId) {
    try {
      if (!selectedShopId) throw new Error('يرجى اختيار متجر أولاً')
      await deleteShopTagMapping(selectedShopId, categoryId)
      message.success('تم الإزالة')
      await fetchAll(selectedShopId)
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const columns = [
    { title: 'رقم', dataIndex: 'id', key: 'id', width: 90 },
    { title: 'التصنيف', dataIndex: 'tagAr', key: 'tagAr' },
    {
      title: 'نوع العرض', dataIndex: 'displayMode', key: 'displayMode', width: 160,
      render: (v) => v === 'UNIFIED_CATALOG'
        ? <AntTag color="purple">كتالوج موحد</AntTag>
        : <AntTag color="blue">قائمة متاجر</AntTag>
    },
    { title: 'مخفي', dataIndex: 'hidden', key: 'hidden', width: 100, render: (v) => v ? <AntTag color="red">مخفي</AntTag> : <AntTag color="blue">ظاهر</AntTag> },
    {
      title: '', key: 'actions', width: 160,
      render: (_, record) => (
        <Space>
          <Popconfirm title="إزالة هذا التصنيف من المتجر؟" onConfirm={() => onRemove(record.id)}>
            <Button danger>إزالة</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 16px' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>المتجر ← التصنيف الرئيسي</Typography.Title>
        <Space>
          <Select
            placeholder="اختر متجرًا"
            style={{ minWidth: 260 }}
            allowClear
            showSearch
            optionFilterProp="label"
            value={selectedShopId || undefined}
            onChange={(v) => setSelectedShopId(v || null)}
            options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))}
          />
          <Button type="primary" onClick={() => setCreateOpen(true)} disabled={!selectedShopId}>تعيين تصنيف</Button>
        </Space>
      </Space>

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={categoriesForSelectedShop()}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="تعيين تصنيف للمتجر"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="تعيين"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate}>
          {!selectedShopId && (
            <Form.Item label="المتجر" name="shopId" rules={[{ required: true, message: 'يرجى اختيار متجر' }]}>
              <Select showSearch optionFilterProp="label" options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))} />
            </Form.Item>
          )}
          <Form.Item label="التصنيف الرئيسي" name="categoryId" rules={[{ required: true, message: 'يرجى اختيار تصنيف' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={availableCategories().map(c => ({ label: c.tagAr, value: c.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
